import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery } from '@tanstack/react-query';


import { getBasketData, refetchBasketData } from '../queries/global';
import { NotificationContext, handleError } from '../context/NotificationContext';

import * as QUERIES from "../queries/query-functions";
import config from "../config/config";
import * as UTILS from "../utils/utils";

import LoadingIndicator from '../components/loading-indicator';
import SelectBestAvailablePrice from './select-best-available-price';
import { SeatSelectionSummaryItem, TotalItem } from './seat-selection-summary';
import TicketStepIcon from '../svg/ticket-step';

export default function BestAvailable({ticketQuantity, bandsByPrice, continueLink, setStepNumber}) {
    const { data: basketData, isFetching: basketDataFetching } = getBasketData(); 

    const {notification, setNotification} = useContext(NotificationContext);
    const [priceListOpen, setPriceListOpen] = useState(false);
    const [resultOpen, setResultOpen] = useState(false);
    const [selectedPrice, setSelectedPrice] = useState(null);    

    // const pricesForm = useRef(null);

    useEffect(() => {
        // Remove selected price if ticket amount changes
        setPriceListOpen(false);
        setResultOpen(false);
        setSelectedPrice(null);
        // pricesForm.current?.reset();
    }, [ticketQuantity]);
    

    const addBestAvailable = useMutation({
        mutationFn: ({ ticketQuantity, bandIds }) => QUERIES.addBestAvailable(ticketQuantity, bandIds),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            refetchBasketData();
            // navigate(props.continueLink);
        },
    })

    const removeTicket = useMutation({
        mutationFn: (ticketsIdArray) => QUERIES.removeTickets(ticketsIdArray),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            refetchBasketData();
        },
    })

    const clearBasket = async () => {
        const tickets = basketData?.tickets || [];
        const ticketsIdArray = UTILS.getTicketsByInstance(tickets).map(t => t.id);
        if (ticketsIdArray.length) {
            setResultOpen(false);
            await removeTicket.mutateAsync(ticketsIdArray);
        }
    }

    const handleSelectPrice = async (band = undefined) => {
        let bandIds = undefined;

        if (band) {
            setSelectedPrice(band);            
            bandIds = band.band_ids            
        }

        await clearBasket();
        
        await addBestAvailable.mutateAsync({
            ticketQuantity,
            bandIds
        });

        setResultOpen(true);
    }

    useEffect(()=>{
        if(!priceListOpen && !resultOpen) {
            setStepNumber('one');
            return;
        }

        if(priceListOpen) {
            setStepNumber('two');
        }

        if(resultOpen) {
            setStepNumber('three');
        }

    }, [priceListOpen, resultOpen])

    return (
        // addBestAvailable.isPending ?
        //     <LoadingIndicator />
            <div className='best-available'>
                {
                    (!priceListOpen && !resultOpen) &&
                    <div className='best-available__box best-available__box--buttons'>
                        <button className='btn btn--primary' 
                            type="button" 
                            onClick={() => {                                
                                handleSelectPrice();
                            }}                            
                            disabled={addBestAvailable.isPending || ticketQuantity == 0}>
                                Find best available seats                        
                        </button>
                        <button className='btn btn--primary booking-pathway__select-quantity--price-panel-button' 
                            disabled={addBestAvailable.isPending ||ticketQuantity == 0} 
                            type="button" 
                            onClick={async () => {
                                await clearBasket();
                                setPriceListOpen(true)
                            }} 
                            aria-controls="priceListDisclosure">
                            Find best prices
                        </button>
                    </div>
                }
                {
                    priceListOpen && <BestAvailablePrice bandsByPrice={bandsByPrice} isPending={addBestAvailable.isPending} ticketQuantity={ticketQuantity} handleSelectPrice={handleSelectPrice} selectedPrice={selectedPrice} setSelectedPrice={setSelectedPrice}/>                    
                }

                { 
                    resultOpen && <BestAvailableResult ticketQuantity={ticketQuantity} continueLink = {continueLink}/> 
                }
        </div>
    )
}

const BestAvailablePrice = ({bandsByPrice, ticketQuantity, handleSelectPrice, selectedPrice, setSelectedPrice, isPending}) => {
    const [loadingAllPrices, setLoadingAllPrices] = useState(true);
    const [pricesPending, setPricesPending] = useState(false);

    useEffect(() => {
        if (!pricesPending) return;
        // Pass loading states from SelectBestAvailablePrice components.
        // If any are pending set a loading state for them all.
        const any_loading = Object.values(pricesPending).some(isLoading => isLoading);

        setLoadingAllPrices(any_loading);

    }, [pricesPending, bandsByPrice]);
    return (
        <div className="best-available__box best-available__box--price" >
            <span className="ticket-step-icon"><TicketStepIcon/></span>
            <div class="booking-pathway__ticket-quantity-heading">
                <h3 class="booking-pathway__header">Which price band would you like to search?</h3>
                <p>We’ll only show you available tickets where you can be seated together.</p>
            </div>
            {/* ref={pricesForm} */}
                <form  className="booking-pathway__pricelist">
                    { (loadingAllPrices || isPending) &&
                        <LoadingIndicator />
                    }
                    {bandsByPrice.map((band) => (
                        <SelectBestAvailablePrice key={band.amount} 
                            band={band} 
                            // priceListOpen={priceListOpen} 
                            selectedPrice={selectedPrice}
                            setSelectedPrice={setSelectedPrice}
                            ticketQuantity={ticketQuantity}
                            pricesPending={pricesPending}
                            setPricesPending={setPricesPending}
                            loadingAllPrices={loadingAllPrices || isPending}
                            addBestAvailableSeat={handleSelectPrice}/>
                    ))}
                </form>
        </div>        
    )
}

const BestAvailableResult = ({ticketQuantity, continueLink}) => {
    const { data: basketData, isFetching: basketDataFetching } = getBasketData(); 
    const [ticketGroup, setTicketGroup] = useState(null);
    const [total, setTotal] = useState(0);
    const { instanceId } = useParams();
    let navigate = useNavigate();

    useEffect(() => {
        if (!basketData || !basketData?.tickets) {
            setTicketGroup(null);
            return;
        }
      
        // To show in the continue button
        let tickets = UTILS.getTicketsByInstance(basketData.tickets);

        if (!tickets?.length) {
            setTicketGroup(null);
            return;
        }      
      
        const grouped_tickets = UTILS.groupTicketsByInstance(tickets);
        const tickets_total = UTILS.getTicketsTotal(tickets);
        setTotal(tickets_total)

        setTicketGroup(grouped_tickets[0]);

    }, [basketData, ticketQuantity]);

    return (    
        <>
        <div className='best-available__box best-available__box--result'>
            <h3 className='booking-pathway__header'>Here are the best seats we found</h3>
            {
                basketDataFetching  && (
                    <LoadingIndicator />
                )
            }
            {ticketGroup ?
                <>
                    <ul role="list" className="basket-summary__items basket-summary__items--seats best-available__result">
                        {ticketGroup && ticketGroup.seats.map((ticket) => (
                            <li key={ticket.ticket.id} className='basket-summary__item best-available__result-item'>
                                <SeatSelectionSummaryItem ticket={ticket} showType={true} />
                            </li>
                        ))}
                        <li className='basket-summary__item basket-summary__total'>
                            <TotalItem total={total} />
                        </li>
                    </ul>

                </>
                : <p>Sorry, we haven't been able to find any seats that match your selection. Please choose your own seats instead or search on another date.</p>
            }
        </div>
        {ticketGroup && (
            <div className='best-available__box best-available__box--buttons'>
                <button className='btn btn--primary' 
                    type="button" 
                    role="link"
                    onClick={() => { 
                        navigate(continueLink);                               
                    }}
                >
                    Secure these tickets                        
                </button>
                <button className='btn' 
                    type="button" 
                    role="link"
                    onClick={() =>
                    navigate(`/book-online/${instanceId}/select-seats?type=best-available`)
                    }
                >
                        View on seat map
                    </button>
            </div>
        )}
        {config.OPTION_FIELDS.concession_message ?
                    <p className='booking-pathway__info-text'>{config.OPTION_FIELDS.concession_message}</p>
            :null}
        </>
    )
}